/**
 * Base32 decode helper for TOTP secrets
 */
function base32ToBuf(str: string): Uint8Array {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  str = str.replace(/=+$/, '').toUpperCase();
  const len = str.length;
  const buf = new Uint8Array(Math.floor((len * 5) / 8));
  let val = 0;
  let count = 0;
  let idx = 0;

  for (let i = 0; i < len; i++) {
    const c = alphabet.indexOf(str[i]);
    if (c === -1) {
      throw new Error('Invalid base32 character in secret');
    }
    val = (val << 5) | c;
    count += 5;
    if (count >= 8) {
      buf[idx++] = (val >> (count - 8)) & 0xff;
      count -= 8;
    }
  }
  return buf;
}

/**
 * Generate a cryptographically secure random Base32 secret string
 */
export function generateSecret(length = 16): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  let secret = '';
  const randomValues = new Uint32Array(length);
  window.crypto.getRandomValues(randomValues);
  for (let i = 0; i < length; i++) {
    secret += alphabet[randomValues[i] % alphabet.length];
  }
  return secret;
}

/**
 * Verify a 6-digit TOTP token against a Base32 secret key
 * Handles time drift (default: ±1 window of 30 seconds)
 */
export async function verifyTOTP(secret: string, code: string, drift = 1): Promise<boolean> {
  try {
    const cleanCode = code.trim().replace(/\s/g, '');
    if (cleanCode.length !== 6 || isNaN(Number(cleanCode))) {
      return false;
    }

    const keyBuf = base32ToBuf(secret);
    const epoch = Math.round(Date.now() / 1000);
    const currentCounter = Math.floor(epoch / 30);

    // Import key for HMAC-SHA1
    const cryptoKey = await window.crypto.subtle.importKey(
      'raw',
      keyBuf as any,
      { name: 'HMAC', hash: 'SHA-1' },
      false,
      ['sign']
    );

    // Check windows within acceptable drift range
    for (let i = -drift; i <= drift; i++) {
      const counter = currentCounter + i;

      // Convert counter integer to 8-byte big-endian array
      const counterBuf = new Uint8Array(8);
      let tmp = counter;
      for (let j = 7; j >= 0; j--) {
        counterBuf[j] = tmp & 0xff;
        tmp = Math.floor(tmp / 256);
      }

      // Compute HMAC signature
      const signature = await window.crypto.subtle.sign(
        'HMAC',
        cryptoKey,
        counterBuf
      );

      const hmacVal = new Uint8Array(signature);
      const offset = hmacVal[hmacVal.length - 1] & 0x0f;

      // Dynamic truncation
      const binary =
        ((hmacVal[offset] & 0x7f) << 24) |
        ((hmacVal[offset + 1] & 0xff) << 16) |
        ((hmacVal[offset + 2] & 0xff) << 8) |
        (hmacVal[offset + 3] & 0xff);

      const otp = (binary % 1000000).toString().padStart(6, '0');
      if (otp === cleanCode) {
        return true;
      }
    }
  } catch (error) {
    console.error('Error verifying TOTP:', error);
  }
  return false;
}
